import {DiagramEntity, DiagramEntityType, DiagramTemplates} from "../types/diagramEntity";
import {
    AbstractMesh,
    Color3,
    CreateGreasedLine,
    Curve3,
    GreasedLineMesh,
    GreasedLineMeshColorMode,
    InstancedMesh,
    Mesh,
    MeshBuilder,
    Scene,
    StandardMaterial,
    Texture,
    Vector3
} from "@babylonjs/core";
import log from "loglevel";
import {v4 as uuidv4} from 'uuid';
import {xyztovec} from "./vectorConversion";
import {AnimatedLineTexture} from "../../util/animatedLineTexture";
import {LightmapGenerator} from "../../util/lightmapGenerator";
import {getToolboxColors} from "../../toolbox/toolbox";
import {findClosestColor} from "../../util/functions/findClosestColor";

// Material sharing statistics
let materialStats = {
    instancesCreated: 0,
    materialsShared: 0,
    materialsFallback: 0
};

export function buildMeshFromDiagramEntity(entity: DiagramEntity, scene: Scene): AbstractMesh {
    const logger = log.getLogger('buildMeshFromDiagramEntity');
    if (!entity) {
        logger.error("buildMeshFromDiagramEntity: entity is null");
        return null;
    }
    switch (entity.type) {
        case DiagramEntityType.USER:
            logger.debug("buildMeshFromDiagramEntity: entity is user");
            break;
        default:
    }
    generateId(entity);

    const newMesh: AbstractMesh = createNewInstanceIfNecessary(entity, scene);

    if (!newMesh) {
        logger.error("buildMeshFromDiagramEntity: newMesh is null", JSON.stringify((entity)));
        return null;
    }
    return mapMetadata(entity, newMesh, scene);
}

function createNewInstanceIfNecessary(entity: DiagramEntity, scene: Scene): AbstractMesh {
    const logger = log.getLogger('createNewInstanceIfNecessary');
    const oldMesh: AbstractMesh = scene.getMeshById(entity.id);
    let newMesh: AbstractMesh;
    if (oldMesh) {
        logger.debug(`mesh ${oldMesh.id} already exists`);
        newMesh = oldMesh;
    } else {
        switch (entity.template) {
            case DiagramTemplates.USER:
                break;
            case DiagramTemplates.IMAGE:
                newMesh = buildImage(entity, scene);
                break;
            case DiagramTemplates.CONNECTION:
                const origin = new Vector3(0, 0, 0);
                const control1 = new Vector3(0, 2, 0);
                const control2 = new Vector3(0, 5, -5);
                const end = new Vector3(0, 5, -8);
                const curve = Curve3.CreateCubicBezier(origin, control1, control2, end, 40);
                const path = curve.getPoints();
                newMesh = CreateGreasedLine(entity.id, {points: path, updatable: true}, {
                    width: .02,
                    colorMode: GreasedLineMeshColorMode.COLOR_MODE_MULTIPLY
                }, scene);
                (newMesh as GreasedLineMesh).intersectionThreshold = 2;
                const material = (newMesh.material as StandardMaterial);
                material.emissiveTexture = AnimatedLineTexture.Texture();
                material.opacityTexture = AnimatedLineTexture.Texture();
                material.disableLighting = true;
                material.metadata = { isConnection: true, preserveTextures: true };  // Preserve animated arrow textures
                newMesh.setEnabled(false);
                break;
            case DiagramTemplates.BOX:
            case DiagramTemplates.SPHERE:
            case DiagramTemplates.CYLINDER:
            case DiagramTemplates.CONE:
            case DiagramTemplates.PLANE:
            case DiagramTemplates.PERSON:
                // Tool meshes are created with UPPERCASE hex codes (BabylonJS toHexString behavior)
                let toolMeshId = "tool-" + entity.template + "-" + entity.color?.toUpperCase();
                let toolMesh = scene.getMeshById(toolMeshId);

                // If exact color match not found, try to find closest color
                if (!toolMesh && entity.color) {
                    const availableColors = getToolboxColors();
                    const closestColor = findClosestColor(entity.color, availableColors);

                    if (closestColor !== entity.color.toLowerCase()) {
                        logger.info(`Color ${entity.color} not found in toolbox, using closest match: ${closestColor}`);
                        // Tool IDs use uppercase hex codes
                        toolMeshId = "tool-" + entity.template + "-" + closestColor.toUpperCase();
                        toolMesh = scene.getMeshById(toolMeshId);

                        if (toolMesh) {
                            logger.info(`Successfully found tool mesh with closest color: ${toolMeshId}`);
                        } else {
                            logger.error(`Even with closest color, tool mesh not found: ${toolMeshId}`);
                        }
                    }
                }

                if (toolMesh && !oldMesh) {
                    // Verify tool mesh has material before creating instance
                    if (!toolMesh.material) {
                        logger.error(`Tool mesh ${toolMeshId} found but has no material! This should never happen.`);
                        logger.error(`Tool mesh state: enabled=${toolMesh.isEnabled()}, parent=${toolMesh.parent?.name}`);
                        // Don't create instance without material
                        break;
                    }

                    logger.debug(`Found tool mesh: ${toolMeshId}, material: ${toolMesh.material.id}`);
                    newMesh = new InstancedMesh(entity.id, (toolMesh as Mesh));
                    // InstancedMesh.material property delegates to sourceMesh.material automatically
                    logger.debug(`Created instance ${entity.id}, inherited material: ${newMesh.material?.id}`);

                    // Track material sharing statistics
                    materialStats.instancesCreated++;
                    if (newMesh.material) {
                        materialStats.materialsShared++;
                    }

                    //                  newMesh.metadata = {template: entity.template, exportable: true, tool: false};
                } else {
                    if (!toolMesh) {
                        logger.warn(`No tool mesh found for ${toolMeshId}. Available tool meshes: ${
                            scene.meshes.filter(m => m.id.startsWith('tool-')).map(m => m.id).slice(0, 5).join(', ')
                        }...`);
                    }
                    if (oldMesh) {
                        logger.debug(`Skipping instance creation, mesh ${entity.id} already exists`);
                    }
                }
                break;
            default:
                logger.warn('no tool mesh found for ' + entity.template + "-" + entity.color);
                break;
        }
        if (newMesh) {
            if (!newMesh.metadata) {
                newMesh.metadata = {template: entity.template, exportable: true, tool: false};
            } else {
                newMesh.metadata.template = entity.template;
                newMesh.metadata.exportable = true;
                newMesh.metadata.tool = false;
            }

            // Store color in metadata so it persists when entity is modified
            if (entity.color) {
                newMesh.metadata.color = entity.color;
            }

        }
    }
    return newMesh;
}

function buildImage(entity: DiagramEntity, scene: Scene): AbstractMesh {
    const logger = log.getLogger('buildImage');
    logger.error(entity);
    logger.debug("buildImage: entity is image");
    const plane = MeshBuilder.CreatePlane(entity.id, {size: 1}, scene);
    const material = new StandardMaterial("planeMaterial", scene);
    material.metadata = { isUI: true };  // Mark as UI to prevent rendering mode modifications
    const image = new Image();
    image.src = entity.image;
    material.emissiveTexture = new Texture(entity.image, scene);
    material.backFaceCulling = false;
    material.disableLighting = true;
    plane.material = material;
    plane.metadata = {image: entity.image};
    plane.scaling = xyztovec(entity.scale);
    image.decode().then(() => {
        plane.scaling.x = image.width / image.height;
    }).catch((error) => {
        logger.error("buildImage: error decoding image", error);
    });
    return plane;
}

function generateId(entity: DiagramEntity) {
    if (!entity.id) {
        entity.id = "id" + uuidv4();
    }
}

function mapMetadata(entity: DiagramEntity, newMesh: AbstractMesh, scene: Scene): AbstractMesh {
    const logger = log.getLogger('mapMetaqdata');
    if (newMesh) {
        if (!newMesh.metadata) {
            newMesh.metadata = {};
        }
        /*if (entity.position) {
            newMesh.position = xyztovec(entity.position);
        }
        if (entity.rotation) {
            if (newMesh.rotationQuaternion) {
                newMesh.rotationQuaternion = Quaternion.FromEulerAngles(entity.rotation.x, entity.rotation.y, entity.rotation.z);
            } else {
                newMesh.rotation = xyztovec(entity.rotation);
            }
        }*/
        /*if (entity.parent) {
            const parent_node = scene.getNodeById(entity.parent);
            if (parent_node) {
                newMesh.parent = parent_node;
                newMesh.metadata.parent = entity.parent;
            }

        }*/
        /*if (entity.scale) {
            newMesh.scaling = xyztovec(entity.scale);
        }*/
        // Material validation - InstancedMesh should automatically inherit material from source mesh
        if (!newMesh.material && newMesh?.metadata?.template != "#object-template") {
            logger.error(`MATERIAL SHARING FAILURE for mesh ${newMesh.id}:`);
            logger.error(`  Template: ${newMesh.metadata?.template}`);
            logger.error(`  Color: ${entity.color}`);
            logger.error(`  Is InstancedMesh: ${newMesh instanceof InstancedMesh}`);

            if (newMesh instanceof InstancedMesh) {
                logger.error(`  Source mesh: ${newMesh.sourceMesh?.id}`);
                logger.error(`  Source mesh material: ${newMesh.sourceMesh?.material?.id || 'MISSING'}`);
                logger.error(`  This indicates tool mesh was created without material!`);
            }

            // Create fallback material as last resort to prevent crashes
            logger.warn(`Creating fallback material to prevent crash - this impacts performance!`);
            newMesh.material = buildMissingMaterial("material-" + entity.id, scene, entity.color);

            // Track fallback material creation
            materialStats.materialsFallback++;
        }

        // Log material sharing statistics periodically
        if (materialStats.instancesCreated > 0 && materialStats.instancesCreated % 10 === 0) {
            const sharingRate = (materialStats.materialsShared / materialStats.instancesCreated * 100).toFixed(1);
            logger.info(`Material Sharing Stats: ${materialStats.materialsShared}/${materialStats.instancesCreated} (${sharingRate}%), Fallbacks: ${materialStats.materialsFallback}`);
        }
        if (entity.text) {
            newMesh.metadata.text = entity.text;
            //updateTextNode(newMesh, entity.text);
        }
        if (entity.from) {
            newMesh.metadata.from = entity.from;
        }
        if (entity.to) {
            newMesh.metadata.to = entity.to;
        }
        if (entity.image) {
            newMesh.metadata.image = entity.image;
        }
    } else {
        logger.error("buildMeshFromDiagramEntity: mesh is null after it should have been created");
    }
    return newMesh;
}


export function buildMissingMaterial(name: string, scene: Scene, color: string): StandardMaterial {
    const existingMaterial = scene.getMaterialById(name);
    if (existingMaterial) {
        return (existingMaterial as StandardMaterial);
    }
    const colorObj = Color3.FromHexString(color);
    const newMaterial = new StandardMaterial(name, scene);
    newMaterial.id = name;

    if (LightmapGenerator.ENABLED) {
        // Lightmap as emissive texture (lighting illusion, no lighting calculations)
        newMaterial.emissiveColor = colorObj;
        newMaterial.emissiveTexture = LightmapGenerator.generateLightmapForColor(colorObj, scene);
        newMaterial.disableLighting = true;
    } else {
        // Flat emissive-only rendering (no lighting illusion)
        newMaterial.emissiveColor = colorObj;
        newMaterial.disableLighting = true;
    }

    newMaterial.alpha = 1;
    return newMaterial;
}